'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ExternalLink, PhoneCall } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatDurationClock } from '@/lib/format';
import { rescueSecondsLeft } from '@/lib/pool-call-rescue';
import { PoolLeaveButton, PoolLeaveLineButton } from '@/components/pool-leave-button';
import { WrapupCountdown } from '@/components/wrapup-countdown';
import { useClientNow } from '@/hooks/use-client-now';
import type { PooledDialerLegView, PooledDialerMemberView } from '../../../shared/types/pooled-dialer';

export function LiveCallStrip({
  membership,
  upNext,
}: {
  membership: {
    campaignId: string;
    member: PooledDialerMemberView;
  } | null;
  upNext: PooledDialerLegView | null;
}) {
  const pathname = usePathname();
  const member = membership?.member ?? null;
  // Parsed auto-Next deadline while wrapping up — the strip must tick for the
  // countdown even though no call is live.
  const wrapupDeadline =
    member?.state === 'wrapup' && member.wrapupDeadline ? Date.parse(member.wrapupDeadline) : null;
  const now = useClientNow(member?.state === 'busy' || wrapupDeadline !== null ? 1000 : null);
  if (!member || !membership) return null;

  // Without the sidebar (below lg) the strip sits under the shell's top bar; on
  // phones it spans the width and the party name takes a row of its own so the
  // timer and buttons stay reachable.
  const frame =
    'pointer-events-auto corner-brand-sm flex max-w-[min(92vw,40rem)] items-center gap-3 border px-4 py-2 shadow-lg shadow-zinc-950/60 max-sm:w-[calc(100vw-1.5rem)] max-sm:max-w-none max-sm:flex-wrap max-sm:gap-y-1';
  const wrap =
    'pointer-events-none fixed left-1/2 z-40 -translate-x-1/2 top-[calc(3.5rem+env(safe-area-inset-top))] lg:top-3';
  const name = 'min-w-0 truncate max-sm:order-first max-sm:basis-full';

  if (member.state === 'busy') {
    const onCard = member.currentContactId && pathname === `/contacts/${member.currentContactId}`;
    if (member.rescueDeadline) {
      const rescueSeconds = now ? rescueSecondsLeft(member.rescueDeadline, now.getTime()) : null;
      // The tab reloaded mid-call: the prospect is on hold while the phone
      // rings this tab back in. The contact card's hang-up still works.
      return (
        <div className={wrap}>
          <div className={cn(frame, 'border-warning/60 bg-zinc-900')} role="status">
            <PhoneCall className="size-4 shrink-0 animate-pulse text-warning" />
            <span className={cn(name, 'text-sm text-zinc-50')}>
              Reconnecting you to {member.currentContactName ?? 'your call'}…
            </span>
            {rescueSeconds !== null ? (
              <span className="shrink-0 text-sm text-warning tabular-nums">{rescueSeconds}s</span>
            ) : null}
          </div>
        </div>
      );
    }
    return (
      <div className={wrap}>
        <div className={cn(frame, 'border-success/60 bg-zinc-900')}>
          <PhoneCall className="size-4 shrink-0 text-success" />
          <span className="text-sm text-success tabular-nums">
            {formatDurationClock(now ? (now.getTime() - Date.parse(member.stateSince)) / 1000 : 0)}
          </span>
          <span className={cn(name, 'text-base font-semibold text-zinc-50')}>
            {member.inbound ? 'Inbound · ' : ''}{member.currentContactName ?? 'Connected'}
          </span>
          {/* The strip is the only prospect surface that survives the screen
              pop's navigation, so it carries the number too: an agent looking
              at a contact page that has not painted yet still has both facts
              they need to open the call. */}
          {member.currentContactPhone ? (
            <span className="shrink-0 text-xs text-zinc-300 tabular-nums">
              {member.currentContactPhone}
            </span>
          ) : null}
          {member.currentContactId && !onCard ? (
            <Link
              href={`/contacts/${member.currentContactId}`}
              className="ml-1 inline-flex shrink-0 items-center gap-1 text-xs text-zinc-300 hover:text-zinc-100"
            >
              <ExternalLink className="size-3.5" /> Open card
            </Link>
          ) : null}
          {/* The stage carries its own copy on the dialer page; two armed
              buttons for one action is one too many. */}
          {pathname === `/campaigns/${membership.campaignId}/dialer` ? null : (
            <PoolLeaveLineButton className="ml-1" />
          )}
        </div>
      </div>
    );
  }

  if (pathname === `/campaigns/${membership.campaignId}/dialer`) return null;

  if (member.state === 'wrapup' || member.state === 'manual') {
    return (
      <div className={wrap}>
        <div className={cn(frame, 'border-warning/40 bg-zinc-900')}>
          <span className="shrink-0 text-sm font-medium text-warning">
            {member.state === 'wrapup' ? 'Wrapping up' : 'Manual work'}
          </span>
          <span className={cn(name, 'text-sm font-semibold text-zinc-100')}>
            {member.currentContactName ?? 'In pool'}
          </span>
          {member.state === 'wrapup' && wrapupDeadline !== null && now ? (
            <WrapupCountdown campaignId={membership.campaignId} deadline={wrapupDeadline} now={now.getTime()} compact />
          ) : null}
          {member.state === 'wrapup' && upNext ? (
            <span className="min-w-0 shrink truncate text-xs text-zinc-400">Up next · {upNext.label}</span>
          ) : null}
          <PoolLeaveButton size="sm" className="ml-1 shrink-0" />
        </div>
      </div>
    );
  }

  if (member.state === 'idle') {
    return (
      <div className={wrap}>
        <div className={cn(frame, 'border-zinc-600 bg-zinc-900')}>
          <span className="shrink-0 text-sm font-medium text-zinc-400">Idle — not taking calls</span>
          <Link
            href={`/campaigns/${membership.campaignId}/dialer`}
            className={cn(name, 'text-sm text-zinc-200 hover:text-zinc-50')}
          >
            Re-join from the pool page
          </Link>
          <PoolLeaveButton size="sm" className="ml-1 shrink-0" />
        </div>
      </div>
    );
  }

  if (member.state === 'free' && upNext) {
    return (
      <div className={wrap}>
        <div className={cn(frame, 'border-warning bg-warning/10')}>
          <span className="shrink-0 text-sm font-medium text-warning">Get ready</span>
          <span className={cn(name, 'text-sm font-semibold text-zinc-100')}>{upNext.label}</span>
        </div>
      </div>
    );
  }

  return null;
}
